import { type OpRecord, type SubmittedOp } from '@dl/protocol';
import {
  DEFAULT_FACT_OWNER,
  DEFAULT_FACT_STATUS,
  FactPayloadSchema,
  HlcClock,
  type FactOwner,
  type FactPayload,
  type FactStatus,
  type FactValidity,
  type OpId,
  viewerKey,
} from '@dl/shared';

import { applyMigrations } from './schema.js';
import { updateSearchIndices } from './search-indices.js';
import { type SqlBackend } from './sql.js';

interface OpRow extends Record<string, unknown> {
  id: OpId;
  subject: string;
  predicate: string;
  payload: string;
  base_version: OpId | null;
  hlc_wall_ms: number;
  hlc_logical: number;
  author: string;
  created_at_real: string;
}

interface HeadRow extends Record<string, unknown> {
  head_op_id: OpId;
  status?: FactStatus;
  owner_kind?: 'canon' | 'belief';
  owner_viewer?: string | null;
  valid_from?: number | null;
  valid_to?: number | null;
}

interface FoldedHeadState {
  status: FactStatus;
  owner: FactOwner;
  validity: FactValidity | null;
}

function parseFactPayloadLoose(payload: unknown): FactPayload | null {
  if (!payload || typeof payload !== 'object') return null;
  const kind = (payload as { kind?: unknown }).kind;
  if (typeof kind !== 'string' || !kind.startsWith('fact.')) return null;
  return FactPayloadSchema.parse(payload);
}

function priorStateFromRow(prior: HeadRow | null): FoldedHeadState {
  if (!prior) return { status: DEFAULT_FACT_STATUS, owner: DEFAULT_FACT_OWNER, validity: null };
  const owner: FactOwner =
    prior.owner_kind === 'belief' && prior.owner_viewer
      ? parseOwnerViewer(prior.owner_viewer)
      : { kind: 'canon' };
  const validity: FactValidity | null =
    (prior.valid_from ?? null) === null && (prior.valid_to ?? null) === null
      ? null
      : {
          ...(prior.valid_from != null ? { from: prior.valid_from } : {}),
          ...(prior.valid_to != null ? { to: prior.valid_to } : {}),
        };
  return {
    status: prior.status ?? DEFAULT_FACT_STATUS,
    owner,
    validity,
  };
}

function parseOwnerViewer(viewerKeyStr: string): FactOwner {
  const sep = viewerKeyStr.indexOf(':');
  if (sep < 0) return { kind: 'canon' };
  const kind = viewerKeyStr.slice(0, sep);
  const id = viewerKeyStr.slice(sep + 1);
  if (kind !== 'character' && kind !== 'party' && kind !== 'user' && kind !== 'gm') {
    return { kind: 'canon' };
  }
  return { kind: 'belief', viewer: { kind, id } };
}

function nextHeadState(prior: HeadRow | null, payload: unknown): FoldedHeadState | null {
  const parsed = parseFactPayloadLoose(payload);
  if (!parsed) return null;
  const priorState = priorStateFromRow(prior);
  switch (parsed.kind) {
    case 'fact.create':
      return {
        status: parsed.status ?? DEFAULT_FACT_STATUS,
        owner: parsed.owner ?? DEFAULT_FACT_OWNER,
        validity: parsed.validity ?? null,
      };
    case 'fact.edit':
      return {
        status: parsed.status ?? priorState.status,
        owner: parsed.owner ?? priorState.owner,
        validity: parsed.validity ?? priorState.validity,
      };
    case 'fact.set_status':
      return { ...priorState, status: parsed.status };
    case 'fact.soft_delete':
    case 'fact.restore':
      return priorState;
  }
}

function rowToRecord<P>(row: OpRow): OpRecord<P> {
  return {
    id: row.id,
    subject: row.subject,
    predicate: row.predicate,
    payload: JSON.parse(row.payload) as P,
    base_version: row.base_version,
    hlc: { wall_ms: row.hlc_wall_ms, logical: row.hlc_logical },
    author: JSON.parse(row.author) as OpRecord<P>['author'],
    created_at_real: row.created_at_real,
  };
}

function recordToRow(record: OpRecord<unknown>): OpRow {
  return {
    id: record.id,
    subject: record.subject,
    predicate: record.predicate,
    payload: JSON.stringify(record.payload),
    base_version: record.base_version,
    hlc_wall_ms: record.hlc.wall_ms,
    hlc_logical: record.hlc.logical,
    author: JSON.stringify(record.author),
    created_at_real: record.created_at_real,
  };
}

/**
 * Mints an op id on the client. The same id is used by the server as
 * the canonical row id — it travels in the wire envelope as
 * `client_op_id` and the DO's `handleOp` passes it through to
 * `appendOp`'s mintId callback. Previously the client minted a
 * `local_<uuid>` provisional id that the server then renamed to
 * `op_<uuid>`; the rename caused `applyServerOp` on the originator to
 * insert a second row alongside the provisional one (different ids =
 * no dedupe via the UNIQUE constraint), yielding duplicate versions
 * in the fold (2026-05-26 history-duplicate bug).
 *
 * With the rename gone, `op_<uuid>` is the single id format end-to-end.
 * The `op_` prefix matches the server's historical mint convention so
 * existing replicas don't see a format break.
 */
export function mintLocalId(): OpId {
  // crypto.randomUUID is available in modern browsers and Node 19+.
  return `op_${crypto.randomUUID()}`;
}

/** Serialize a server-confirmed HLC for the durable-outbox `confirmed_hlc` column. */
function serializeConfirmedHlc(hlc: { wall_ms: number; logical: number }): string {
  return JSON.stringify({ wall_ms: hlc.wall_ms, logical: hlc.logical });
}

/**
 * The GM client's local replica of the canonical op log. Async-externally /
 * sync-internally (per 11-tech-stack.md §5) — public methods are async so
 * the same surface works whether the SqlBackend is in-process (node tests)
 * or behind a Web Worker (browser runtime via Comlink).
 *
 * The replica writes provisional ops locally on appendLocal(), then replays
 * server-accepted ops on applyServerOp(). The fold is implicit in the
 * predicate_head table; rebuilding it from op_log is the source-of-truth
 * test that B5 (#10) gates the phase on.
 */
export class Replica {
  private readonly clock = new HlcClock();

  constructor(private readonly sql: SqlBackend) {
    applyMigrations(this.sql);
  }

  /**
   * Append an op authored locally on this client. The op gets a provisional
   * id + HLC; the server replaces them on acceptance. The chain head is
   * updated optimistically so subsequent local ops on the same chain see a
   * consistent view.
   */
  async appendLocal(op: SubmittedOp<unknown>, mintId: () => OpId = mintLocalId): Promise<OpRecord<unknown>> {
    const id = mintId();
    const hlc = this.clock.now();
    const record: OpRecord<unknown> = {
      id,
      subject: op.subject,
      predicate: op.predicate,
      payload: op.payload,
      base_version: op.base_version,
      hlc,
      author: op.author,
      created_at_real: op.created_at_real,
    };
    // Locally authored — unconfirmed until the server accepts it (confirmed_hlc
    // NULL). The durable outbox re-enqueues it on reconnect if the ack was lost.
    this.insertOp(record, null);
    this.upsertHead(record);
    this.maintainSearchIndices(record);
    return record;
  }

  /**
   * Apply a server-accepted op to the local replica. Idempotent: when
   * the same op id already exists locally (the originator's own ops
   * round-trip back via op_accept — same id, since the server now uses
   * the client's id as canonical), we update only the HLC (the server
   * is the authority on cross-client ordering, even when it preserves
   * the client's id).
   *
   * The op_log id is shared between client and server because the
   * server uses `client_op_id` from the wire envelope as the row id —
   * see `apps/do-workspace/src/sync-handler.ts:handleOp`. Earlier the
   * server minted its own `op_<uuid>` id and the originator's
   * `applyServerOp` inserted a second row beside the provisional one,
   * causing the fold to yield duplicate fact versions (2026-05-26
   * "two history entries with the same timestamp" report).
   */
  async applyServerOp(record: OpRecord<unknown>): Promise<void> {
    this.applyServerOpSync(record);
  }

  /**
   * Apply a BATCH of server ops in a single call (#sync perf). The WS
   * backfill streams one op_push frame per op; applying each via a
   * separate `applyServerOp` is one Comlink round-trip per op (≈hundreds
   * for a large campaign → multi-second backfill). This applies the whole
   * batch in one round-trip, wrapped in a single transaction so the OPFS
   * layer fsyncs once instead of per-op. Records must already be in HLC
   * order (the caller buffers them in arrival order). Idempotent per op,
   * same as `applyServerOp`.
   */
  async applyServerOps(records: readonly OpRecord<unknown>[]): Promise<void> {
    if (records.length === 0) return;
    if (records.length === 1) {
      this.applyServerOpSync(records[0]!);
      return;
    }
    this.sql.exec('BEGIN;');
    try {
      for (const record of records) this.applyServerOpSync(record);
      this.sql.exec('COMMIT;');
    } catch (err) {
      this.sql.exec('ROLLBACK;');
      throw err;
    }
  }

  private applyServerOpSync(record: OpRecord<unknown>): void {
    this.clock.merge(record.hlc);
    const existing = this.getOpById(record.id);
    if (existing) {
      // Own provisional landed earlier with the client-side HLC. The
      // server's HLC is the canonical ordering field — overwrite it
      // (and the base_version, in case the server's view differed
      // from the client's optimistic read) so the local op log
      // matches the server's view exactly. Payload, subject,
      // predicate are unchanged — those are immutable parts of the
      // logical write and the server doesn't rewrite them.
      // The server accepted this op (own provisional round-trip). Stamp the
      // canonical HLC/base_version AND mark it confirmed in the durable outbox so
      // a reload no longer re-enqueues it (audit fix).
      this.sql.exec(
        `UPDATE op_log
           SET hlc_wall_ms = ?, hlc_logical = ?, base_version = ?, confirmed_hlc = ?
           WHERE id = ?;`,
        record.hlc.wall_ms,
        record.hlc.logical,
        record.base_version,
        serializeConfirmedHlc(record.hlc),
        record.id,
      );
      return;
    }
    // A brand-new op arriving from the server (a peer's op or backfill) is
    // authoritative on arrival — insert it already-confirmed.
    this.insertOp(record, record.hlc);
    this.upsertHead(record);
    this.maintainSearchIndices(record);
  }

  /**
   * Remove a locally-authored provisional op the server REJECTED (stale or
   * invariant) so it stops being folded as canon. Without this, the rejected
   * op stays in the local op_log; because the client clock merges server HLCs
   * its stamp sits at/above the server tip, so `iterateOpLog` folds the rejected
   * value as the current head — forever, on every reload — silently letting a
   * write the server refused "win" locally (violating "no silent overwrites").
   *
   * Deletes the op and rebuilds its (subject, predicate) chain's head + search
   * indices from the remaining ops, in one transaction. No-op if the id is
   * absent (e.g. an already-superseded row). Chain rebuild reuses the exact
   * incremental `upsertHead` / `updateSearchIndices` logic, replayed in HLC
   * order, so the result is identical to never having applied the op.
   */
  async removeLocalOp(id: OpId): Promise<void> {
    const row = this.getOpById(id);
    if (!row) return;
    const record = rowToRecord(row);
    this.sql.exec('BEGIN;');
    try {
      this.sql.exec('DELETE FROM op_log WHERE id = ?;', id);
      this.rebuildHead(record.subject, record.predicate);
      // updateSearchIndices reloads the chain from op_log (now without the
      // deleted op) and rebuilds the name/FTS entries from the remaining fold.
      this.maintainSearchIndices(record);
      this.sql.exec('COMMIT;');
    } catch (err) {
      this.sql.exec('ROLLBACK;');
      throw err;
    }
  }

  /**
   * Recompute a chain's `predicate_head` row from scratch by replaying every
   * remaining op for (subject, predicate) in HLC order through `upsertHead`
   * (which reads the running head from the DB). If no ops remain, the head row
   * is dropped. Used after `removeLocalOp` deletes a rejected op.
   */
  private rebuildHead(subject: string, predicate: string): void {
    this.sql.exec(
      'DELETE FROM predicate_head WHERE subject = ? AND predicate = ?;',
      subject,
      predicate,
    );
    const rows = this.sql.all<OpRow>(
      `SELECT * FROM op_log WHERE subject = ? AND predicate = ?
        ORDER BY hlc_wall_ms ASC, hlc_logical ASC, id ASC;`,
      subject,
      predicate,
    );
    for (const r of rows) this.upsertHead(rowToRecord(r));
  }

  /**
   * Current head op id for a (subject, predicate) chain — the value the
   * client must supply as `base_version` on the next append to that chain.
   */
  async getHead(subject: string, predicate: string): Promise<OpId | null> {
    const row = this.sql.one<HeadRow>(
      `SELECT head_op_id FROM predicate_head WHERE subject = ? AND predicate = ?;`,
      subject,
      predicate,
    );
    return row?.head_op_id ?? null;
  }

  /**
   * Every op on ONE (subject, predicate) chain, in HLC order.
   *
   * Scoped counterpart to `iterateOpLog` for callers that need a fact's CURRENT
   * state before appending to it. The replica is authoritative and synchronous
   * here, whereas a caller's folded view of the world is refreshed
   * asynchronously — so a read-modify-write that sources its "current value"
   * from that view can act on state that is merely stale. Fold what this
   * returns with the shared entity fold; never re-derive current-value rules
   * locally.
   */
  async getChainOps(subject: string, predicate: string): Promise<OpRecord<unknown>[]> {
    const rows = this.sql.all<OpRow>(
      `SELECT * FROM op_log WHERE subject = ? AND predicate = ?
        ORDER BY hlc_wall_ms ASC, hlc_logical ASC, id ASC;`,
      subject,
      predicate,
    );
    return rows.map(rowToRecord);
  }

  /**
   * Iterate the entire op log in HLC order. Used by the replay test
   * (B5 / #10) and by the projection function when rebuilding cache state.
   * Returned as a plain array at WS scope; revisit when op counts approach
   * the "stream me" threshold (~10k+).
   */
  async iterateOpLog(): Promise<OpRecord<unknown>[]> {
    const rows = this.sql.all<OpRow>(
      `SELECT * FROM op_log ORDER BY hlc_wall_ms ASC, hlc_logical ASC;`,
    );
    return rows.map((r) => rowToRecord(r));
  }

  /**
   * The highest HLC present in the local op log, or null when empty
   * (III.A.2 / #80). The SyncClient clamps its resume watermark to this
   * so a persisted watermark that advanced past a not-yet-durable op
   * write can't cause the since-watermark backfill to skip ops the local
   * log never received.
   */
  async maxOpHlc(): Promise<{ wall_ms: number; logical: number } | null> {
    const row = this.sql.one<{ hlc_wall_ms: number; hlc_logical: number }>(
      `SELECT hlc_wall_ms, hlc_logical FROM op_log
        ORDER BY hlc_wall_ms DESC, hlc_logical DESC LIMIT 1;`,
    );
    if (!row) return null;
    return { wall_ms: Number(row.hlc_wall_ms), logical: Number(row.hlc_logical) };
  }

  /**
   * List every locally-authored op still awaiting server arbitration (durable
   * outbox — audit fix). These are the `op_log` rows with a NULL `confirmed_hlc`
   * (written by `appendLocal`, not yet stamped by `applyServerOp`). The
   * `SyncClient` re-enqueues them on `connect()` so a submit→reload→reconnect
   * cannot silently drop a write that is still folded as canon. Returned in HLC
   * order (submission order for a single author).
   */
  async listUnconfirmedOps(): Promise<OpRecord<unknown>[]> {
    const rows = this.sql.all<OpRow>(
      `SELECT * FROM op_log WHERE confirmed_hlc IS NULL
        ORDER BY hlc_wall_ms ASC, hlc_logical ASC, id ASC;`,
    );
    return rows.map((r) => rowToRecord(r));
  }

  /**
   * Mark a locally-authored op confirmed (durable outbox — audit fix), stamping
   * the server-confirmed HLC into `confirmed_hlc` so it is no longer re-enqueued
   * on reconnect. `applyServerOp` already does this on the accept round-trip;
   * this is the explicit entry point for callers that confirm out of band.
   * No-op if the id is absent.
   */
  async markOpConfirmed(id: OpId, hlc: { wall_ms: number; logical: number }): Promise<void> {
    this.sql.exec(
      `UPDATE op_log SET confirmed_hlc = ? WHERE id = ?;`,
      serializeConfirmedHlc(hlc),
      id,
    );
  }

  /**
   * Streaming bootstrap. Replays an async iterator of accepted ops into the
   * replica, calling `onProgress` periodically so the UI can show "1k of 50k
   * synced …" during first-device bootstrap.
   */
  async initialReplicate(
    stream: AsyncIterable<OpRecord<unknown>>,
    onProgress?: (applied: number) => void,
  ): Promise<number> {
    let applied = 0;
    for await (const op of stream) {
      await this.applyServerOp(op);
      applied++;
      if (onProgress && applied % 100 === 0) onProgress(applied);
    }
    if (onProgress) onProgress(applied);
    return applied;
  }

  /**
   * Read the persisted per-Viewer sync watermark (III.A.2 / #62). The
   * client passes this to the server on `hello` to resume the
   * projected stream. Returns null when no watermark has been
   * persisted yet.
   */
  async getViewerWatermark(viewerKeyStr: string): Promise<{ wall_ms: number; logical: number } | null> {
    const row = this.sql.one<{ hlc_wall_ms: number; hlc_logical: number }>(
      `SELECT hlc_wall_ms, hlc_logical FROM viewer_watermarks WHERE viewer_key = ?;`,
      viewerKeyStr,
    );
    if (!row) return null;
    return { wall_ms: Number(row.hlc_wall_ms), logical: Number(row.hlc_logical) };
  }

  /**
   * Advance the per-Viewer sync watermark. Monotonic — a regression
   * is dropped (defensive against malformed server frames). Called by
   * the SyncClient after each successfully applied wire frame.
   */
  async setViewerWatermark(
    viewerKeyStr: string,
    hlc: { wall_ms: number; logical: number },
  ): Promise<void> {
    this.sql.exec(
      `INSERT INTO viewer_watermarks (viewer_key, hlc_wall_ms, hlc_logical, updated_at)
         VALUES (?, ?, ?, ?)
       ON CONFLICT (viewer_key) DO UPDATE SET
         hlc_wall_ms = excluded.hlc_wall_ms,
         hlc_logical = excluded.hlc_logical,
         updated_at = excluded.updated_at
       WHERE
         excluded.hlc_wall_ms > viewer_watermarks.hlc_wall_ms
         OR (excluded.hlc_wall_ms = viewer_watermarks.hlc_wall_ms
             AND excluded.hlc_logical > viewer_watermarks.hlc_logical);`,
      viewerKeyStr,
      hlc.wall_ms,
      hlc.logical,
      new Date().toISOString(),
    );
  }

  /** Close the underlying SqlBackend. */
  async close(): Promise<void> {
    this.sql.close();
  }

  /**
   * Insert an op row. `confirmedHlc` records the server-confirmed HLC in the
   * durable outbox column (audit fix): `null` for a locally-authored provisional
   * op that still needs server arbitration; the op's own HLC for a
   * server-accepted op (peer op or own accept round-trip). `listUnconfirmedOps`
   * keys off the NULL.
   */
  private insertOp(record: OpRecord<unknown>, confirmedHlc: { wall_ms: number; logical: number } | null): void {
    const row = recordToRow(record);
    this.sql.exec(
      `INSERT INTO op_log
         (id, subject, predicate, payload, base_version, hlc_wall_ms, hlc_logical, author, created_at_real, confirmed_hlc)
       VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?);`,
      row.id,
      row.subject,
      row.predicate,
      row.payload,
      row.base_version,
      row.hlc_wall_ms,
      row.hlc_logical,
      row.author,
      row.created_at_real,
      confirmedHlc ? serializeConfirmedHlc(confirmedHlc) : null,
    );
  }

  private maintainSearchIndices(record: OpRecord<unknown>): void {
    // I.E.2 / I.E.3 — keep replica's search indices in lockstep with the
    // op log. The server side does the same inside `appendOp`; here we
    // run on every locally-appended or server-accepted op so the GM's
    // queries hit a current index without round-tripping the network.
    updateSearchIndices(this.sql, record.subject, record.predicate, record.payload);
  }

  private upsertHead(record: OpRecord<unknown>): void {
    const prior = this.sql.one<HeadRow>(
      `SELECT head_op_id, status, owner_kind, owner_viewer, valid_from, valid_to
         FROM predicate_head WHERE subject = ? AND predicate = ?;`,
      record.subject,
      record.predicate,
    );
    const headState = nextHeadState(prior, record.payload);
    if (headState) {
      const ownerViewer =
        headState.owner.kind === 'belief' ? viewerKey(headState.owner.viewer) : null;
      this.sql.exec(
        `INSERT INTO predicate_head
           (subject, predicate, head_op_id, status, owner_kind, owner_viewer, valid_from, valid_to)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?)
         ON CONFLICT (subject, predicate) DO UPDATE SET
           head_op_id = excluded.head_op_id,
           status = excluded.status,
           owner_kind = excluded.owner_kind,
           owner_viewer = excluded.owner_viewer,
           valid_from = excluded.valid_from,
           valid_to = excluded.valid_to;`,
        record.subject,
        record.predicate,
        record.id,
        headState.status,
        headState.owner.kind,
        ownerViewer,
        headState.validity?.from ?? null,
        headState.validity?.to ?? null,
      );
    } else {
      this.sql.exec(
        `INSERT INTO predicate_head (subject, predicate, head_op_id)
           VALUES (?, ?, ?)
         ON CONFLICT (subject, predicate) DO UPDATE SET head_op_id = excluded.head_op_id;`,
        record.subject,
        record.predicate,
        record.id,
      );
    }
  }

  private getOpById(id: OpId): OpRow | null {
    return this.sql.one<OpRow>(`SELECT * FROM op_log WHERE id = ?;`, id);
  }
}
